import { TicketingFormCategory } from "@simplyk/common";

import { useIsOnboarding } from "@/common/hooks/useIsOnboarding";
import { TicketingOutput } from "@/common/types/trpc";
import { LiveEditorStepKey } from "@/features/LiveFormEditor/Steps/LiveEditorStep.enum";

export const useGetAllTicketingStepsInOrder = ({
  apiTicketing,
  editOrCreateMode,
  isAddOnsStepOn = false,
  isCampaignLifeCycleStepOn = false,
}: {
  apiTicketing: TicketingOutput;
  editOrCreateMode: "edit" | "create";
  isAddOnsStepOn?: boolean;
  isCampaignLifeCycleStepOn?: boolean;
}): { steps: { key: LiveEditorStepKey; shouldBeSkip?: boolean }[] } => {
  const { isOnboarding } = useIsOnboarding();

  const formCategory = apiTicketing.formCategory;

  const isFree = isFreeTicketing(apiTicketing);
  const allRatesArePwyc = areAllRatesPayWhatYouCan(apiTicketing);

  const businessModelStepShouldBeSkip = isFree || !isOnboarding || editOrCreateMode === "edit";

  if (!formCategory) {
    return { steps: [] };
  }

  switch (formCategory) {
    case TicketingFormCategory.Event:
      return {
        steps: [
          { key: "title" },
          { key: "date" },
          { key: "location" },
          { key: "description" },
          { key: "color" },
          { key: "banner" },
          { key: "logo" },
          { key: "tickets" },
          ...(isAddOnsStepOn ? [{ key: "add-ons" as const }] : []),
          { key: "questions" as const },
          ...(isAddOnsStepOn ? [] : [{ key: "add-donation" as const }]),
          { key: "discount-codes", shouldBeSkip: isFree || allRatesArePwyc },
          {
            key: "business-model",
            shouldBeSkip: businessModelStepShouldBeSkip,
          },
          { key: "e-ticket" },
          { key: "automated-communications" },
          { key: "advanced-settings" },
          { key: "review" },
        ],
      };
    case TicketingFormCategory.Auction:
      return {
        steps: [
          { key: "title" },
          { key: "date" },
          { key: "description" },
          { key: "color" },
          { key: "banner" },
          { key: "logo" },
          { key: "order-fulfillment" },
          { key: "tickets" },
          { key: "questions" as const },
          {
            key: "business-model",
            shouldBeSkip: businessModelStepShouldBeSkip,
          },
          { key: "automated-communications" },
          { key: "advanced-settings" },
          { key: "review" },
        ],
      };
    case TicketingFormCategory.Shop:
      return {
        steps: [
          { key: "title" },
          { key: "description" },
          { key: "color" },
          { key: "banner" },
          { key: "logo" },
          { key: "order-fulfillment" },
          { key: "tickets" },
          { key: "questions" },
          { key: "add-donation" },
          { key: "discount-codes", shouldBeSkip: isFree || allRatesArePwyc },
          {
            key: "business-model",
            shouldBeSkip: businessModelStepShouldBeSkip,
          },
          { key: "automated-communications" },
          { key: "advanced-settings" },
          { key: "review" },
        ],
      };
    case TicketingFormCategory.MembershipV2:
      return {
        steps: [
          { key: "campaign-life-cycle", shouldBeSkip: !isCampaignLifeCycleStepOn },
          { key: "title" },
          { key: "description" },
          { key: "color" },
          { key: "banner" },
          { key: "logo" },
          { key: "tickets" },
          { key: "add-donation" },
          { key: "questions" },
          { key: "discount-codes", shouldBeSkip: isFree || allRatesArePwyc },
          {
            key: "business-model",
            shouldBeSkip: businessModelStepShouldBeSkip,
          },
          { key: "e-ticket" },
          { key: "automated-communications" },
          { key: "advanced-settings" },
          { key: "review" },
        ],
      };
    case TicketingFormCategory.Lottery:
      return {
        steps: [
          { key: "title" },
          { key: "date" },
          { key: "prizes" },
          { key: "description" },
          { key: "color" },
          { key: "banner" },
          { key: "logo" },
          { key: "tickets" },
          { key: "questions" },
          { key: "add-donation" },
          { key: "discount-codes", shouldBeSkip: isFree || allRatesArePwyc },
          {
            key: "business-model",
            shouldBeSkip: businessModelStepShouldBeSkip,
          },
          { key: "e-ticket" },
          { key: "automated-communications" },
          { key: "advanced-settings" },
          { key: "review" },
        ],
      };
    case TicketingFormCategory.Sweepstakes:
      return {
        steps: [
          { key: "title" },
          { key: "date" },
          { key: "prizes" },
          { key: "description" },
          { key: "color" },
          { key: "banner" },
          { key: "logo" },
          { key: "tickets" },
          { key: "questions" },
          { key: "add-donation" },
          { key: "discount-codes", shouldBeSkip: true },
          {
            key: "business-model",
            shouldBeSkip: businessModelStepShouldBeSkip,
          },
          { key: "e-ticket" },
          { key: "automated-communications" },
          { key: "advanced-settings" },
          { key: "review" },
        ],
      };
    case TicketingFormCategory.Custom:
      return {
        steps: [
          { key: "title" },
          { key: "date" },
          { key: "location" },
          { key: "fundraising-goal" },
          { key: "description" },
          { key: "color" },
          { key: "banner" },
          { key: "logo" },
          { key: "tickets" },
          { key: "questions" },
          { key: "add-donation" },
          { key: "discount-codes", shouldBeSkip: isFree || allRatesArePwyc },
          {
            key: "business-model",
            shouldBeSkip: businessModelStepShouldBeSkip,
          },
          { key: "e-ticket" },
          { key: "automated-communications" },
          { key: "advanced-settings" },
          { key: "review" },
        ],
      };
    default:
      throw new Error(`Form category ${formCategory} not supported`);
  }
};

const isFreeTicketing = (apiTicketing: TicketingOutput) => {
  if (!apiTicketing) {
    return false;
  }

  return !apiTicketing.rates?.some((rate) => rate.amount);
};

const areAllRatesPayWhatYouCan = (apiTicketing: TicketingOutput) => {
  if (!apiTicketing?.rates?.length) {
    return false;
  }

  return apiTicketing.rates.every((rate) => (rate as { isPayWhatYouCan?: boolean }).isPayWhatYouCan);
};
